(function () {
  const PROP = {
    GROUP: '_addon_group',
    PARENT_VARIANT: '_parent_variant_id',
    PARENT_PRODUCT: '_parent_product_id',
    TYPE: '_addon_type',
    PRODUCT_TYPE: '_product_type'
  };

  /** Normalize Shopify routes root for cart API URLs. */
  try {
    const r = String(window.Shopify?.routes?.root || '');
    if (/^https?:|^\/\//.test(r) || (r && !r.startsWith('/'))) window.Shopify.routes.root = '/';
  } catch {}

  /** Return a safe shop-relative root path. */
  const getRoot = () => {
    const r = String(window.Shopify?.routes?.root || '/');
    return r.startsWith('http') || r.startsWith('//') ? '/' : r.startsWith('/') ? r : `/${r}`;
  };

  /** Get the charm selector custom element on the page. */
  const $selector = () => document.querySelector('cust-charm-selector');

  /** Get the PDP add-to-cart form in the same section. */
  const $form = (el) => el?.closest('.shopify-section')?.querySelector('form[action*="/cart/add"]');

  /** Read the main product variant id from the PDP form. */
  const parentVariantId = (el) => $form(el)?.querySelector('[name="id"]')?.value || '';

  /** Read the main product quantity from the PDP form. */
  const parentQty = (el) => Math.max(1, parseInt($form(el)?.querySelector('[name="quantity"]')?.value || '1', 10) || 1);

  /** Flatten confirmed charms, birthstones, chains and with quantity. */
  const selections = (el) => {
    if (!el?.getSelections) return [];
    const s = el.getSelections();
    return [...(s.charms || []), ...(s.birthstones || []), ...(s.chains || [])].filter((i) => i.quantity > 0);
  };

  /** Build sessionStorage key for parent–cart link state. */
  const linkKey = (id) => `cust-charm:cartLinked:${id}`;

  /** Check if this parent variant was previously linked to the cart. */
  const isLinked = (id) => {
    try {
      return sessionStorage.getItem(linkKey(id)) === '1';
    } catch {
      return false;
    }
  };

  /** Mark or clear parent–cart link state in sessionStorage. */
  const setLinked = (id, on) => {
    try {
      on ? sessionStorage.setItem(linkKey(id), '1') : sessionStorage.removeItem(linkKey(id));
    } catch {}
  };

  let cartByKey = new Map();
  let syncTimer;
  let cleanupBusy;

  /** Cache cart line items by line key for quick lookup. */
  const rememberCart = (cart) => {
    cartByKey = new Map((cart?.items || []).filter((i) => i.key).map((i) => [i.key, i]));
  };

  /** Fetch current cart JSON. */
  const fetchCart = () =>
    fetch(`${getRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Cache-Control': 'no-cache' },
    }).then((r) => {
      if (!r.ok) throw new Error(`cart.js failed (${r.status})`);
      return r.json();
    });

  /** POST quantity updates to cart/update.js. */
  const updateCart = (updates) => {
    if (!Object.keys(updates || {}).length) return Promise.resolve();
    return fetch(`${getRoot()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates }),
    }).then((r) => {
      if (!r.ok) throw new Error(`cart/update.js failed (${r.status})`);
    });
  };

  /** Build line item properties linking add-ons to the parent. */
  const addonProps = (gid, parentId, productId, type) => ({
    [PROP.GROUP]: String(gid),
    [PROP.PARENT_VARIANT]: String(parentId),
    [PROP.PARENT_PRODUCT]: String(productId || ''),
    [PROP.TYPE]: String(type || ''),
    [PROP.PRODUCT_TYPE]: 'Add-on product',
  });

  /** Remove all cart add-ons tied to a parent variant. */
  const removeAddons = async (parentId) => {
    const cart = await fetchCart();
    const updates = {};
    (cart.items || []).forEach((it) => {
      if (!it.properties?.[PROP.GROUP]) return;
      if (String(it.properties[PROP.PARENT_VARIANT]) !== String(parentId)) return;
      updates[it.key] = 0;
    });
    return updateCart(updates);
  };

  /** Create a unique add-on group id for one parent bundle. */
  const newGroupId = () =>
    crypto.randomUUID?.() || `addon-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  /** Collect section ids for bundled cart drawer re-render. */
  const sectionsParam = () => {
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }),
    );
    const id = document.querySelector('cart-drawer')?.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
    if (id) sections.add(id);
    return [...sections].join(',');
  };

  /** Map PDP selections to cart/add.js line item payloads. */
  const addonLineItems = (addons, gid, parentId, productId) =>
    addons.map((a) => ({
      id: Number(a.variantId),
      quantity: Number(a.quantity),
      properties: addonProps(gid, parentId, productId, a.type),
    }));

  /** Add multiple items and refresh or open the cart drawer. */
  const addItems = async (items, baseForm) => {
    const fd = baseForm instanceof FormData ? baseForm : new FormData();
    fd.set('sections', sectionsParam());
    fd.delete('id');
    fd.delete('quantity');
    items.forEach((item, i) => {
      fd.append(`items[${i}][id]`, String(item.id));
      fd.append(`items[${i}][quantity]`, String(item.quantity));
      Object.entries(item.properties || {}).forEach(([k, v]) => fd.append(`items[${i}][properties][${k}]`, String(v)));
    });

    const res = await fetch(`${getRoot()}cart/add.js`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    const json = await res.json().catch(() => {
      throw new Error(`cart/add.js failed (${res.status})`);
    });
    if (!res.ok) throw new Error(json.description || json.message || 'Add to cart failed');

    if (window.themeVariables?.settings?.cartType === 'drawer') {
      if (json.sections) {
        const cart = await fetchCart();
        cart.sections = json.sections;
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { baseEvent: 'variant:add', cart } }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
      setTimeout(() => document.querySelector('cart-drawer')?.show?.(), 450);
    }
    return json;
  };

  /** Run async work with theme loading overlay on/off. */
  const withLoading = async (fn) => {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      return await fn();
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  };

  /** Show an error toast message. */
  const toast = (msg) => document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: msg, tone: 'error' } }));

  /** Clear PDP selections and remove orphaned add-ons after parent removal. */
  const cleanupParent = async (selector, parentId) => {
    if (cleanupBusy) return;
    cleanupBusy = true;
    try {
      await removeAddons(parentId);
      selector?.setSelectionsFromCart?.([]);
      setLinked(parentId, false);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch {
      /* ignore */
    } finally {
      cleanupBusy = false;
    }
  };

  /** Read the formatted add-on price from the selector drawer card. */
  const addonPriceFormatted = (selector, variantId) => {
    if (!selector || !variantId) return '';
    const card = selector.querySelector(`[data-drawer-item][data-variant-id="${CSS.escape(String(variantId))}"]`);
    return card?.getAttribute('data-price-formatted') || '';
  };

  /** Map cart line items to PDP selection objects for one parent. */
  const mapAddons = (cart, parentId, selector) =>
    (cart.items || [])
      .filter((it) => it.properties?.[PROP.GROUP] && String(it.properties[PROP.PARENT_VARIANT]) === String(parentId))
      .map((it) => ({
        variantId: Number(it.variant_id),
        productId: Number(it.product_id),
        quantity: Number(it.quantity || 0),
        title: it.product_title || it.title || '',
        type: it.properties?.[PROP.TYPE] || 'charms' || 'birthstones' || 'chains',
        image: it.featured_image?.url || '',
        price: Number(it.final_price ?? it.price ?? 0),
        priceFormatted: addonPriceFormatted(selector, it.variant_id),
      }));

  /** Sync PDP UI from cart; clean up if parent was removed. */
  const syncFromCart = async () => {
    const selector = $selector();
    if (!selector?.setSelectionsFromCart) return;

    const pid = parentVariantId(selector);
    if (!pid) return;

    const cart = await fetchCart();
    rememberCart(cart);

    const hasParent = (cart.items || []).some(
      (it) => !it.properties?.[PROP.GROUP] && String(it.variant_id) === String(pid),
    );
    const addons = mapAddons(cart, pid, selector);

    if (!hasParent && isLinked(pid)) {
      await cleanupParent(selector, pid);
      return;
    }

    if (hasParent || addons.length) {
      setLinked(pid, true);
      selector.setSelectionsFromCart(addons);
    }
  };

  /** Intercept PDP add-to-cart and checkout when add-ons are selected. */
  document.addEventListener(
    'submit',
    async (e) => {
      const selector = $selector();
      const addons = selections(selector);
      if (!selector || !addons.length) return;

      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const pid = parentVariantId(selector);
      const productId = selector.dataset.parentProductId || '';
      const gid = newGroupId();
      const lines = addonLineItems(addons, gid, pid, productId);

      if (form.action.includes('/cart/add')) {
        if (!selector.closest('.shopify-section')?.contains(form) || !form.closest('product-form')) return;
        const mainId = form.querySelector('[name="id"]')?.value;
        if (!mainId) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        try {
          await withLoading(async () => {
            await removeAddons(mainId);
            await addItems(
              [{ id: Number(mainId), quantity: parentQty(selector), properties: { [PROP.PRODUCT_TYPE]: 'Main product' } }, ...lines],
              new FormData(form),
            );
          });
        } catch (err) {
          toast(err.message || 'Could not add to cart');
        }
        return;
      }

      if (e.submitter?.name !== 'checkout' || !form.action?.includes('/cart') || !pid) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      try {
        await withLoading(async () => {
          const cart = await fetchCart();
          const hasParent = (cart.items || []).some(
            (it) => !it.properties?.[PROP.GROUP] && String(it.variant_id) === String(pid),
          );
          const items = hasParent
            ? lines
              : [{ id: Number(pid), quantity: parentQty(selector), properties: { [PROP.PRODUCT_TYPE]: 'Main product' } }, ...lines];
          await removeAddons(pid);
          await addItems(items);
          window.location.href = `${getRoot()}checkout`;
        });
      } catch (err) {
        toast(err.message || 'Could not proceed to checkout');
      }
    },
    true,
  );

  /** Clear cart add-ons when merchant empties PDP selections. */
  document.addEventListener('cust-charm-selector:change', async () => {
    const selector = $selector();
    const pid = parentVariantId(selector);
    if (!selector || !pid || selections(selector).length || !isLinked(pid)) return;
    await cleanupParent(selector, pid);
  });

  /** On parent line removal in cart drawer: clear PDP and drop linked add-ons. */
  document.addEventListener(
    'line-item:will-change',
    (e) => {
      const line = e.target;
      if (line?.tagName !== 'LINE-ITEM' || !line.closest('cart-drawer')) return;
      if (Number(e.detail?.targetQuantity) !== 0 || line.closest('.cust-charm-cart__addon-item')) return;

      const pid = cartByKey.get(line.querySelector('[data-line-key]')?.getAttribute('data-line-key'))?.variant_id;
      if (!pid) return;

      const selector = $selector();
      const pdpId = selector ? parentVariantId(selector) : '';
      if (selector && pdpId && String(pid) === String(pdpId)) {
        selector.setSelectionsFromCart([]);
        selector.style.display = 'none';
        cleanupParent(selector, pid).finally(() => {
          selector.style.display = '';
        });
        return;
      }
      removeAddons(pid);
    },
    true,
  );

  /** Debounced cart → PDP sync after cart updates. */
  document.addEventListener('cart:change', (e) => {
    if (e.detail?.cart) rememberCart(e.detail.cart);
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncFromCart, 150);
  });

  /** Debounced cart → PDP sync after cart drawer HTML refresh. */
  document.addEventListener('cart-drawer:refreshed', () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncFromCart, 150);
  });

  /** Prime cart line cache on load when charm selector is present. */
  const boot = () => $selector() && fetchCart().then(rememberCart).catch(() => {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
